import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './LandingPage.css';

/* ── Animated molecule particle canvas ───────────────────────── */
const ParticleCanvas: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current!;
    const ctx = canvas.getContext('2d')!;
    let animId: number;

    const resize = () => {
      canvas.width  = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    interface Dot { x: number; y: number; vx: number; vy: number; r: number; }
    const dots: Dot[] = Array.from({ length: 60 }, () => ({
      x:  Math.random() * canvas.width,
      y:  Math.random() * canvas.height,
      vx: (Math.random() - 0.5) * 0.4,
      vy: (Math.random() - 0.5) * 0.4,
      r:  Math.random() * 2 + 1,
    }));

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      for (let i = 0; i < dots.length; i++) {
        for (let j = i + 1; j < dots.length; j++) {
          const dx = dots[i].x - dots[j].x;
          const dy = dots[i].y - dots[j].y;
          const dist = Math.hypot(dx, dy);
          if (dist < 120) {
            ctx.strokeStyle = `rgba(0,220,200,${1 - dist / 120})`;
            ctx.lineWidth = 0.5;
            ctx.beginPath();
            ctx.moveTo(dots[i].x, dots[i].y);
            ctx.lineTo(dots[j].x, dots[j].y);
            ctx.stroke();
          }
        }
      }

      dots.forEach((d) => {
        ctx.beginPath();
        ctx.arc(d.x, d.y, d.r, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(0,220,200,0.7)';
        ctx.fill();

        d.x += d.vx;
        d.y += d.vy;
        if (d.x < 0 || d.x > canvas.width)  d.vx *= -1;
        if (d.y < 0 || d.y > canvas.height) d.vy *= -1;
      });

      animId = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return <canvas ref={canvasRef} className="lp-canvas" />;
};

/* ── Main component ──────────────────────────────────────────── */
const LandingPage: React.FC = () => {
  const navigate = useNavigate();

  /* Scroll to a section by id. The navbar-height offset is handled by
     `scroll-margin-top` in CSS (see .lp-features/.lp-stats/.lp-about),
     so scrollIntoView lands in the right spot without manual math that
     can be thrown off by fonts/layout not being fully settled yet. */
  const scrollToSection = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    window.history.replaceState(null, '', `#${id}`);
  };

  /* If the page is loaded directly with a hash in the URL (a deep link,
     or a full refresh on /#about), the browser's one-shot native hash
     scroll usually fires before React has rendered the target section,
     so it silently does nothing. Retry manually once mounted — a couple
     of animation frames plus a longer fallback covers late web-font /
     layout shifts. */
  useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    if (!hash) return;

    const scrollToHash = () => {
      const el = document.getElementById(hash);
      if (el) el.scrollIntoView({ behavior: 'auto', block: 'start' });
    };

    const raf = requestAnimationFrame(() => requestAnimationFrame(scrollToHash));
    const timer = setTimeout(scrollToHash, 400); // fallback for late font/layout shifts

    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(timer);
    };
  }, []);

  const features = [
    { icon: 'fas fa-user-injured',      title: 'Patient Management',  desc: 'Securely register, manage and track patient records, demographics and test histories in one place.',          color: '#00dcc8' },
    { icon: 'fas fa-file-medical-alt',  title: 'Lab Reports',         desc: 'Generate, preview and share digital test reports instantly with configurable header templates.',              color: '#7c6ffd' },
    { icon: 'fas fa-receipt',           title: 'Billing & Payments',  desc: 'Streamline invoicing, payment collection and financial reporting with detailed audit trails.',                 color: '#f59e0b' },
    { icon: 'fas fa-flask',             title: 'Test Catalogue',      desc: 'Maintain test categories, types and reference ranges with department-wise organisation.',                      color: '#ef4444' },
    { icon: 'fas fa-chart-line',        title: 'Finance Reports',     desc: 'Monitor income by reference, track expenses and view comprehensive financial summaries.',                      color: '#10b981' },
    { icon: 'fas fa-user-shield',       title: 'Role-Based Access',   desc: 'Assign granular permissions per user type to keep data secure and workflows efficient.',                       color: '#3b82f6' },
  ];

  const stats = [
    { value: '10k+',  label: 'Patients Managed'   },
    { value: '50k+',  label: 'Reports Generated'  },
    { value: '99.9%', label: 'Uptime'             },
    { value: '24/7',  label: 'Support Ready'      },
  ];

  return (
    <div className="lp-root">

      {/* ── Navbar ─────────────────────────────────────────────── */}
      <nav className="lp-nav">
        <div className="lp-nav-inner">
          <div className="lp-logo">
            <i className="fas fa-microscope lp-logo-icon" />
            <span>Ezy<strong>Lab</strong></span>
          </div>
          <div className="lp-nav-links">
            <a href="#features" onClick={(e) => scrollToSection(e, 'features')}>Features</a>
            <a href="#stats" onClick={(e) => scrollToSection(e, 'stats')}>Stats</a>
            <a href="#about" onClick={(e) => scrollToSection(e, 'about')}>About</a>
          </div>
          <button id="lp-login-btn" className="lp-btn-login" onClick={() => navigate('/login')}>
            <i className="fas fa-sign-in-alt me-2" />
            Login
          </button>
        </div>
      </nav>

      {/* ── Hero ───────────────────────────────────────────────── */}
      <section className="lp-hero">
        <ParticleCanvas />
        <div className="lp-hero-content">
          <div className="lp-badge">
            <i className="fas fa-circle-notch fa-spin" style={{ marginRight: 8, fontSize: '0.7rem' }} />
            Laboratory Information System
          </div>
          <h1 className="lp-headline">
            Smart Laboratory<br />
            <span className="lp-gradient-text">Management System</span>
          </h1>
          <p className="lp-sub">
            Streamline operations, enhance efficiency and deliver accurate results.<br />
            The modern platform built for digital pathology and lab workflows.
          </p>
          <div className="lp-cta-group">
            <button className="lp-btn-primary" onClick={() => navigate('/login')}>
              <i className="fas fa-rocket" style={{ marginRight: 8 }} />
              Get Started
            </button>
            <a href="#features" className="lp-btn-ghost" onClick={(e) => scrollToSection(e, 'features')}>
              <i className="fas fa-play-circle" style={{ marginRight: 8 }} />
              Learn More
            </a>
          </div>
        </div>
        <div className="lp-hero-scroll-hint">
          <i className="fas fa-chevron-down" />
        </div>
      </section>

      {/* ── Stats bar ──────────────────────────────────────────── */}
      <section id="stats" className="lp-stats">
        {stats.map((s) => (
          <div key={s.label} className="lp-stat-item">
            <span className="lp-stat-value">{s.value}</span>
            <span className="lp-stat-label">{s.label}</span>
          </div>
        ))}
      </section>

      {/* ── Features ───────────────────────────────────────────── */}
      <section id="features" className="lp-features">
        <div className="lp-section-header">
          <span className="lp-section-badge">Features</span>
          <h2>Everything Your Lab Needs</h2>
          <p>One platform. All workflows. Zero compromise.</p>
        </div>
        <div className="lp-cards">
          {features.map((f) => (
            <div key={f.title} className="lp-card" style={{ '--accent': f.color } as React.CSSProperties}>
              <div className="lp-card-icon">
                <i className={f.icon} />
              </div>
              <h3>{f.title}</h3>
              <p>{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── About / CTA ────────────────────────────────────────── */}
      <section id="about" className="lp-about">
        <div className="lp-about-inner">
          <div className="lp-about-text">
            <span className="lp-section-badge">About EzyLab</span>
            <h2>Built for Modern Labs,<br />Designed for People</h2>
            <p>
              EzyLab is a full-featured laboratory information system that simplifies every aspect
              of lab management — from patient registration to billing, report generation to finance
              analytics. Built with a focus on speed, accuracy and ease of use.
            </p>
            <ul className="lp-about-list">
              <li><i className="fas fa-check-circle" /> Cloud-ready and offline-capable</li>
              <li><i className="fas fa-check-circle" /> Customisable header templates for reports</li>
              <li><i className="fas fa-check-circle" /> Real-time billing and payment tracking</li>
              <li><i className="fas fa-check-circle" /> Secure role-based access control</li>
            </ul>
          </div>
          <div className="lp-about-cta-card">
            <i className="fas fa-microscope lp-cta-icon" />
            <h3>Ready to Modernise<br />Your Lab?</h3>
            <p>Log in to your EzyLab dashboard and take full control of your laboratory operations today.</p>
            <button className="lp-btn-primary" style={{ width: '100%' }} onClick={() => navigate('/login')}>
              <i className="fas fa-sign-in-alt" style={{ marginRight: 8 }} />
              Login to Dashboard
            </button>
          </div>
        </div>
      </section>

      {/* ── Footer ─────────────────────────────────────────────── */}
      <footer className="lp-footer">
        <div className="lp-footer-inner">
          <div className="lp-logo" style={{ color: '#fff' }}>
            <i className="fas fa-microscope lp-logo-icon" />
            <span>Ezy<strong>Lab</strong></span>
          </div>
          <p className="lp-footer-copy">© {new Date().getFullYear()} EzyLab. All rights reserved.</p>
        </div>
      </footer>

    </div>
  );
};

export default LandingPage;